import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForecast } from '@/hooks/useOpportunities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatCurrency } from '@/lib/utils'
import { TrendingUp, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'

type Period = 'this_month' | 'next_month' | 'this_quarter' | 'this_year'

const PERIOD_LABELS: Record<Period, string> = {
  this_month: 'Este mês',
  next_month: 'Próximo mês',
  this_quarter: 'Trimestre atual',
  this_year: 'Ano atual',
}

function getPeriodRange(period: Period): { start: string; end: string; label: string } {
  const today = new Date()
  const y = today.getFullYear()
  const m = today.getMonth()
  const pad = (n: number) => String(n).padStart(2, '0')
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

  if (period === 'this_month') {
    const start = new Date(y, m, 1)
    const end = new Date(y, m + 1, 0)
    return { start: fmt(start), end: fmt(end), label: start.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }) }
  }
  if (period === 'next_month') {
    const start = new Date(y, m + 1, 1)
    const end = new Date(y, m + 2, 0)
    return { start: fmt(start), end: fmt(end), label: start.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }) }
  }
  if (period === 'this_quarter') {
    const q = Math.floor(m / 3)
    const start = new Date(y, q * 3, 1)
    const end = new Date(y, q * 3 + 3, 0)
    return { start: fmt(start), end: fmt(end), label: `T${q + 1}/${y}` }
  }
  const start = new Date(y, 0, 1)
  const end = new Date(y, 11, 31)
  return { start: fmt(start), end: fmt(end), label: String(y) }
}

const STAGE_LABELS: Record<string, string> = {
  qualified: 'Qualificado',
  proposal_sent: 'Proposta enviada',
  negotiation: 'Negociação',
  closing: 'Fechamento',
}

export function ForecastSection() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<Period>('this_month')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const range = useMemo(() => getPeriodRange(period), [period])
  const { data, isLoading, error } = useForecast(range.start, range.end)

  const toggle = (ownerId: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(ownerId)) next.delete(ownerId)
      else next.add(ownerId)
      return next
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Previsão de fechamento
            <span className="text-sm font-normal text-muted-foreground">— {range.label}</span>
          </CardTitle>
          <div className="flex items-center gap-1 bg-muted rounded-md p-1">
            {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  period === p ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Carregando previsão…
          </div>
        )}
        {error && (
          <div className="text-sm text-destructive py-4">Erro ao carregar previsão</div>
        )}
        {data && !isLoading && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-3 border-b">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Bruto</div>
                <div className="text-2xl font-bold">{formatCurrency(data.totals.gross)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Ponderado (× probabilidade)</div>
                <div className="text-2xl font-bold text-primary">{formatCurrency(data.totals.weighted)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Oportunidades abertas</div>
                <div className="text-2xl font-bold">{data.totals.count}</div>
              </div>
            </div>

            {data.owners.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">
                Nenhuma oportunidade com previsão de fechamento neste período.
              </div>
            )}

            {data.owners.map(owner => {
              const isExp = expanded.has(owner.owner_id)
              const pctOfTotal = data.totals.gross > 0 ? (owner.gross_total / data.totals.gross) * 100 : 0
              return (
                <div key={owner.owner_id} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggle(owner.owner_id)}
                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                  >
                    {isExp ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                    {owner.owner_avatar_url ? (
                      <img
                        src={`https://social.myncc.com.br${owner.owner_avatar_url}`}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {owner.owner_name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{owner.owner_name}</div>
                      <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${pctOfTotal}%` }} />
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold">{formatCurrency(owner.gross_total)}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(owner.weighted_total)} ponderado · {owner.count} {owner.count === 1 ? 'opp' : 'opps'}
                      </div>
                    </div>
                  </button>
                  {isExp && (
                    <div className="border-t bg-muted/30">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-muted-foreground border-b">
                            <th className="text-left px-3 py-1.5 font-normal">Oportunidade</th>
                            <th className="text-left px-3 py-1.5 font-normal">Estágio</th>
                            <th className="text-right px-3 py-1.5 font-normal">Valor</th>
                            <th className="text-right px-3 py-1.5 font-normal">Prob.</th>
                            <th className="text-right px-3 py-1.5 font-normal">Fechamento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {owner.opportunities.map(opp => (
                            <tr
                              key={opp.id}
                              className="border-b last:border-0 hover:bg-muted cursor-pointer"
                              onClick={() => navigate(`/opportunities/${opp.id}`)}
                            >
                              <td className="px-3 py-1.5 max-w-[320px]">
                                {opp.company_name && (
                                  <div className="text-xs text-muted-foreground truncate">{opp.company_name}</div>
                                )}
                                <div className="truncate font-medium">{opp.name}</div>
                              </td>
                              <td className="px-3 py-1.5 text-muted-foreground">{STAGE_LABELS[opp.stage] || opp.stage}</td>
                              <td className="px-3 py-1.5 text-right">{formatCurrency(opp.value)}</td>
                              <td className="px-3 py-1.5 text-right text-muted-foreground">{opp.probability}%</td>
                              <td className="px-3 py-1.5 text-right text-muted-foreground text-xs">
                                {opp.expected_close_date ? new Date(opp.expected_close_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
